import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { Theme, ThemeProvider } from './ThemeProvider';
import { useSettings } from '../modules/Settings/SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';

interface ThemeWrapperProps {
  children: ReactNode;
}

/**
 * Wrapper that connects ThemeProvider to the settings system.
 * - Reads initial theme from server settings
 * - Syncs theme changes back to server
 */
export const ThemeWrapper = ({ children }: ThemeWrapperProps) => {
  const { settings } = useSettings();
  const { mutate: saveSettings } = useSaveSettings();
  
  // Use ref to access latest settings without triggering callback recreation
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const handleThemeChange = useCallback(
    (theme: Theme) => {
      // Update settings with new theme using ref to avoid dependency on settings
      saveSettings({
        ...settingsRef.current,
        theme,
      });
    },
    [saveSettings]
  );

  // Memoize serverTheme to prevent unnecessary re-renders
  const serverTheme = useMemo(() => settings.theme, [settings.theme]);

  return (
    <ThemeProvider serverTheme={serverTheme} onThemeChange={handleThemeChange}>
      {children}
    </ThemeProvider>
  );
};
